import jsPDF from "jspdf";
import moment from "moment";
import "jspdf-autotable";
import { useEffect, useState } from "react";
import { DOCUMENT_TYPES } from "../constants/CreateDocumentFormData";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { FileOpener } from "@capacitor-community/file-opener";
import { Capacitor } from "@capacitor/core";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { SpeechRecognition } from "@capacitor-community/speech-recognition";

const html2pdf = require("html2pdf.js");

export const isMobilePlatform = () => {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
};
const isRunningOnMobile = Capacitor.getPlatform() !== "web";
export const IS_DEVELOPMENT =
  process.env.REACT_APP_BASE_URL.includes("development") ||
  process.env.REACT_APP_BASE_URL.includes("localhost");
export const IS_CHROME =
  /Chrome\/\d+/.test(navigator.userAgent) &&
  !/Edg\//.test(navigator.userAgent) && // exclude Edge
  !/OPR\//.test(navigator.userAgent); // exclude Opera

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isMobile;
};

const truncateText = (text) => {
  if (text.length > 50) {
    return text.substring(0, 50) + "...";
  } else {
    return text;
  }
};
// Helper function untuk konversi Blob ke Base64
const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64data = reader.result.split(",")[1]; // ambil hanya bagian base64
      resolve(base64data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export const dateNowUnix = () => {
  return moment.unix(Math.floor(Date.now() / 1000));
};
export const dateUnixToDateFormatted = (timestamp) => {
  return moment.unix(timestamp).format("yyyy-MM-DD");
};
export const dateStringToUnix = (date) => {
  return moment.unix(Math.floor(Date.parse(date) / 1000)).unix();
};

export const dateStringToDateFormatted = (date, format) => {
  return moment
    .unix(Math.floor(Date.parse(date) / 1000))
    .format(format ?? "yyyy-MM-DD");
};

export const formatDate = (timestamp, format) => {
  return moment.unix(timestamp).format(format ?? "YYYY-MM-DD");
};

export const formatDateIso = (isoString) => {
  const date = new Date(isoString);
  return date
    .toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
    .replace(/\//g, "-");
};
export const formatCurrency = (val) => {
  return val.replace(/\D/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};
const formatCurrencyIDR = (num) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR" }).format(
    num,
  );

export const formatRupiah = (value) => {
  if (!value) return "Rp 0";
  return `Rp ${parseInt(value, 10).toLocaleString("id-ID")}`;
};
export const toNumber = (value) => {
  try {
    return parseInt(value.replace(/\./g, ""));
  } catch (error) {
    return 0;
  }
};
export const getImageBase64 = async (imageUrl) => {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = function () {
        resolve(reader.result.split(",")[1]); // Ambil Base64 tanpa prefix
      };

      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Error loading image:", error);
    return null;
  }
};
export const getFavicon = (url) => {
  return `https://www.google.com/s2/favicons?domain=${url}&sz=128`;
};

/// PRESCRIPTION - A6 Layout
export const generatePrescriptionPDF = async ({
  prescription,
  logo,
  viewOnly = false,
}) => {
  const toastId = toast.loading("Sedang menyiapkan dokumen...");
  await new Promise((resolve) => setTimeout(resolve, 1000));
  try {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "A6",
    });

    // === HEADER: Nama Dokter Sebagai Judul ===
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(prescription.clinic.name || "Klinik", 52.5, 8, {
      align: "center",
    });

    // SIP (dummy) dan Alamat Klinik
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.text(`${prescription.doctor.sip || ""}`, 52.5, 12, { align: "center" });
    doc.text(
      // Gunakan ternary operator untuk mengecek dan memotong string alamat
      `${truncateText(prescription.clinic.address || "")} Telp.${
        prescription.clinic.phone || "-"
      }`,
      52.5,
      16,
      {
        align: "center",
      },
    );

    // Garis pembatas
    doc.setDrawColor(0);
    doc.setLineWidth(0.2);
    doc.line(0, 20.5, 105, 20.5);

    // Tanggal & Judul Resep
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("Resep Obat", 52.5, 26, { align: "center" });

    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.text(dayjs().format("D MMM YYYY"), 95, 26, { align: "right" });

    // Informasi Pasien
    let y = 30;
    doc.setFont("helvetica", "normal");
    doc.text(`Nama Pasien: ${prescription.patient.name || "-"}`, 10, y);
    y += 4;
    doc.text(`Nama Dokter: ${prescription.doctor.name || "-"}`, 10, y);
    y += 4;
    doc.text(
      `Tanggal Lahir: ${
        new Date(prescription.patient.dob).toLocaleDateString("id-ID") || "-"
      }`,
      10,
      y,
    );
    y += 4;
    doc.text(`Jenis Kelamin: ${prescription.patient.gender || "-"}`, 10, y);
    y += 4;

    // Daftar Resep
    doc.setFont("helvetica", "bold");
    doc.text("R/", 10, y);
    y += 3;

    const prescriptions = prescription.selectedPrescriptions || [];
    const customPrescriptions = prescription.selectedCustomPrescriptions || [];

    const medicines = [
      ...prescriptions.map((p) => [
        p.medicine.name,
        p.qty,
        p.unit,
        p.instruction,
      ]),
      ...customPrescriptions.map((p) => [p.name, p.qty, p.unit, p.instruction]),
    ];

    // Fallback jika kosong semua

    doc.autoTable({
      startY: y,
      head: [["No.", "Obat", "Jml", "Sat", "Instruksi"]],
      body: medicines.map((row, idx) => [idx + 1, ...row]),
      theme: "grid",
      headStyles: {
        fillColor: false,
        textColor: 0,
        fontStyle: "bold",
        fontSize: 7,
        lineWidth: 0.2,
        lineColor: 0,
      },
      styles: {
        fontSize: 7,
        cellPadding: 1.2,
        textColor: 0,
        lineColor: 0,
        lineWidth: 0.1,
        overflow: "linebreak",
      },
      columnStyles: {
        0: { cellWidth: 6 },
        1: { cellWidth: 35 },
        2: { cellWidth: 10 },
        3: { cellWidth: 10 },
        4: { cellWidth: 25 },
      },
      margin: { left: 10, right: 10 },
      tableWidth: "wrap",
    });

    // Catatan
    let noteY = doc.autoTable.previous.finalY + 5;
    if (prescription.notes) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.text("Catatan:", 10, noteY);
      noteY += 4;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.text(`${prescription.notes}`, 10, noteY, {
        maxWidth: 85,
      });
    }

    // Footer
    const footerY = 142;
    doc.setFont("helvetica", "italic");
    doc.setFontSize(6);
    doc.setTextColor(130);
    doc.text(
      `Ditandatangani digital oleh ${
        prescription.doctor.name || "-"
      }, via Perigigi.com`,
      52.5, // Tengah halaman A6 (105mm / 2)
      footerY,
      { align: "center" },
    );

    doc.setTextColor(0);

    // Output
    const filename = `Resep_Obat_${prescription.patient.name}.pdf`;
    if (viewOnly) {
      const blobUrl = doc.output("bloburl");
      window.open(blobUrl);
      return;
    }

    if (isRunningOnMobile) {
      const pdfBlob = doc.output("blob");
      const base64Data = await blobToBase64(pdfBlob);
      const savedFile = await Filesystem.writeFile({
        path: filename,
        data: base64Data,
        directory: Directory.Documents,
      });
      console.log("PDF berhasil disimpan di:", savedFile.uri);
      await FileOpener.open({
        filePath: savedFile.uri,
        contentType: "application/pdf",
      });
    } else {
      doc.save(filename);
    }
    toast.update(toastId, {
      render: "Dokumen berhasil dibuat!",
      type: "success",
      isLoading: false,
      autoClose: 3000,
    });
  } catch (error) {
    toast.update(toastId, {
      render: "Gagal membuat dokumen: ",
      type: "error",
      isLoading: false,
      autoClose: 4000,
    });
  }
};

///REPORT
export const generateReportPDF = async ({
  exam,
  fromDate,
  toDate,
  patientType,
  ReportsPatientType,
  includeFinance,
  logo,
}) => {
  const docReport = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  try {
    if (logo) {
      const base64data = await getImageBase64(logo);
      if (base64data) {
        docReport.addImage(base64data, "PNG", 14, 14, 14, 14);
      } else {
        console.warn(
          "Logo tidak dapat dimuat atau bukan format base64 yang valid.",
        );
      }
    }
  } catch (error) {
    console.error("Gagal memuat logo:", error);
  }

  const totalPay = exam.reduce((acc, item) => {
    const payment = item.Payment?.[0]?.totalPayment || 0;
    return acc + payment;
  }, 0);

  // **Informasi Klinik**
  const clinic = exam?.[0]?.clinic || {};
  docReport.setFontSize(12);
  docReport.setFont("helvetica", "bold");
  docReport.text(clinic.name || "-", 30, 15);
  docReport.setFontSize(10);
  docReport.setFont("helvetica", "normal");
  docReport.text(
    `${truncateText(clinic.address || "-")}, ${clinic.city || "-"}`,
    30,
    21,
  );
  docReport.text(`${clinic.zipCode || "-"}`, 30, 27);
  docReport.text(`Telp: ${clinic.phone || "-"}`, 30, 32);

  // **Tanggal Laporan**
  const currentDate = formatDate(Math.floor(Date.now() / 1000), "DD-MM-YYYY");
  docReport.text(currentDate, docReport.internal.pageSize.width - 10, 15, {
    align: "right",
  });

  docReport.setFontSize(10);
  docReport.text(
    `Dari: ${formatDate(fromDate, "DD-MM-YYYY")} - Sampai: ${formatDate(
      toDate,
      "DD-MM-YYYY",
    )}`,
    docReport.internal.pageSize.width - 10,
    26,
    { align: "right" },
  );

  // **Garis Pemisah**
  docReport.setLineWidth(0.5);
  docReport.line(10, 38, docReport.internal.pageSize.width - 10, 38);

  // **Judul Laporan**
  docReport.setFontSize(20);
  docReport.setFont("helvetica", "bold");
  docReport.text(
    "Laporan Pemeriksaan",
    docReport.internal.pageSize.width / 2,
    48,
    {
      align: "center",
    },
  );

  // **Definisi Kolom Tabel**
  const tableColumn = [
    "No",
    "Tanggal",
    "Nama Pasien",
    "Tanggal Lahir",
    "Nama Dokter",
    ...(patientType === ReportsPatientType.NON_BPJS ? [] : ["No Kartu BPJS"]),
    "Kode Diagnosis",
    "Kode Tindakan",
    ...(includeFinance ? ["Metode Pembayaran"] : []),
    ...(includeFinance ? ["Biaya"] : []),
  ];

  // **Pembuatan Baris Tabel**
  const tableRows = exam.map((item, index) => {
    const subTotal = item.Payment?.[0]?.totalPayment || "-";
    const paymentMethod =
      item.Payment?.[0]?.method === "NEW"
        ? "Tunai"
        : item.Payment?.[0]?.method || "-";

    // --- LOGIKA UNTUK DIAGNOSIS (Menggabungkan & Mengelompokkan) ---
    const allDiagnoses = [
      ...(item.selectedDiagnosis || []),
      ...(item.selectedCustomDiagnosis || []),
    ];

    const groupedDiagnoses = allDiagnoses.reduce((acc, d) => {
      const { diagnosis, position } = d;

      const key =
        position && typeof position === "string" && position.trim() !== ""
          ? position.trim()
          : "Diagnosis Umum";

      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(diagnosis.join(","));
      return acc;
    }, {});

    // Membuat array string untuk tampilan (e.g., "Gigi 12: Karies, Pulpitis", "Diagnosis Umum: Yes, Oku")
    const formattedDiagnoses = Object.keys(groupedDiagnoses).map(
      (positionKey) => {
        // Untuk posisi gigi spesifik
        return `${positionKey}:\n${groupedDiagnoses[positionKey].join(", ")}\n`;
      },
    );

    // Gabungkan semua baris diagnosis dengan newline
    const diagnosisText = formattedDiagnoses.join("\n");

    // --- LOGIKA UNTUK ICD ---
    const icdItems = item.selectedIcd
      ? item.selectedIcd.map((e) => `${e.code} ${e.display}`).filter(Boolean)
      : [];
    // Gabungkan semua baris ICD dengan newline
    const icdText = icdItems.join("\n");

    return [
      index + 1,
      formatDate(item.examinationDate, "DD-MMMM-YYYY"),
      item.patient.name || "-",
      formatDateIso(item.patient.dob) || "-",
      item.doctor?.name || "-",
      ...(patientType === ReportsPatientType.NON_BPJS
        ? []
        : [item.patient.idBpjs || "-"]),
      diagnosisText,
      icdText,
      ...(includeFinance ? [paymentMethod] : []),
      ...(includeFinance ? [`Rp ${subTotal.toLocaleString("id-ID")}`] : []),
    ];
  });

  // **Render Tabel Menggunakan autoTable**
  docReport.autoTable({
    head: [tableColumn],
    body: tableRows,
    startY: 58,
    theme: "grid",
    styles: {
      fillColor: false,
      textColor: [0, 0, 0],
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
      valign: "top",
      fontSize: 8,
      cellPadding: 1,
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: false,
      textColor: [0, 0, 0],
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
      fontStyle: "bold",
      halign: "center",
    },
    alternateRowStyles: {
      fillColor: false,
    },
    columnStyles: {
      6: { cellWidth: 50, halign: "left" },
      7: { cellWidth: 50, halign: "left" },
      ...(includeFinance
        ? { [tableColumn.length - 1]: { cellWidth: 30, halign: "right" } }
        : {}),
    },
    // Menghapus didDrawCell hook karena tidak ada bullet points lagi
    // didDrawCell: function (data) { ... } // Dihapus
    margin: { top: 10, right: 10, bottom: 10, left: 10 },
  });

  // Tambahkan ringkasan total pembayaran
  if (includeFinance) {
    let finalY =
      docReport.autoTable.previous.finalY ||
      docReport.internal.pageSize.height - 20;
    docReport.setFontSize(12);
    docReport.setFont("helvetica", "bold");
    docReport.text(
      `Total : Rp ${totalPay.toLocaleString("id-ID")}`,
      docReport.internal.pageSize.width - 14,
      finalY + 10,
      { align: "right" },
    );
  }

  // **Simpan PDF**
  const fileName = `Laporan_${clinic.name.replace(/\s+/g, "_")}_${formatDate(
    fromDate,
  )}_to_${formatDate(toDate)}.pdf`;

  if (isRunningOnMobile) {
    const pdfBlob = docReport.output("blob");
    const base64Data = await blobToBase64(pdfBlob);

    const savedFile = await Filesystem.writeFile({
      path: fileName,
      data: base64Data,
      directory: Directory.Documents,
    });

    console.log("PDF berhasil disimpan di:", savedFile.uri);
    await FileOpener.open({
      filePath: savedFile.uri,
      contentType: "application/pdf",
    });
  } else {
    docReport.save(fileName);
  }
};

///ODONTOGRAM
export const generateOdontogramPDF = async ({
  svgElement,
  patient,
  viewOnly = false,
}) => {
  if (!svgElement) {
    console.error("SVG element not found");
    return;
  }
  const svgOuterHTML = svgElement.outerHTML;
  html2pdf()
    .from(svgOuterHTML)
    .set({
      filename: `Odontogram_${patient.name}.pdf`,
      image: { type: "jpeg", quality: 1 },
      html2canvas: { scale: 1 },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    })
    .toPdf()
    .get("pdf")
    .then(async (generatedPdf) => {
      const filename = `Odontogram_${patient.name}.pdf`;

      if (viewOnly) {
        const blobUrl = generatedPdf.output("bloburl");
        window.open(blobUrl);
        return;
      }
      if (isRunningOnMobile) {
        const pdfBlob = generatedPdf.output("blob");
        const base64Data = await blobToBase64(pdfBlob);
        const savedFile = await Filesystem.writeFile({
          path: filename,
          data: base64Data,
          directory: Directory.Documents,
        });

        // Buka PDF setelah disimpan
        await FileOpener.open({
          filePath: savedFile.uri,
          contentType: "application/pdf",
        });
      } else {
        generatedPdf.save(filename);
      }
    });
};

///OHIS
export const generateOhisPDF = async ({
  patient,
  svgElement,
  viewOnly = false,
}) => {
  if (!svgElement) {
    console.error("element not found");
    return;
  }

  html2pdf()
    .from(svgElement)
    .set({
      filename: `OHIS_${patient.name}.pdf`,
      image: { type: "jpeg", quality: 1 },
      html2canvas: { scale: 1 },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    })
    .toPdf()
    .get("pdf")
    .then(async (generatedPdf) => {
      const filename = `OHIS_${patient.name}.pdf`;
      if (viewOnly) {
        const blobUrl = generatedPdf.output("bloburl");
        window.open(blobUrl);
        return;
      }
      if (isRunningOnMobile) {
        const pdfBlob = generatedPdf.output("blob");
        const base64Data = await blobToBase64(pdfBlob);
        const savedFile = await Filesystem.writeFile({
          path: filename,
          data: base64Data,
          directory: Directory.Documents,
        });

        // Buka PDF setelah disimpan
        await FileOpener.open({
          filePath: savedFile.uri,
          contentType: "application/pdf",
        });
      } else {
        generatedPdf.save(filename);
      }
    });
};

export function convertToWebp(file, onProgress) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = function (e) {
      onProgress(30); // Step 1: File loaded
      const img = new Image();
      img.onload = function () {
        onProgress(60); // Step 2: Image ready
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        // Convert to WebP
        canvas.toBlob(
          (blob) => {
            onProgress(100);
            resolve(blob);
          },
          "image/webp",
          0.5, // quality
        );
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

export async function convertMultipleToWebp(files) {
  const blobs = [];

  for (let i = 0; i < files.length; i++) {
    const blob = await convertToWebp(files[i]);
    blobs.push(blob);
  }

  return blobs;
}
export const AuthStorage = {
  set: (key, value) => localStorage.setItem(key, value),
  get: (key) => localStorage.getItem(key),
  remove: (key) => localStorage.removeItem(key),
  clear: () => localStorage.clear(),
};
export const showToast = (message) => {
  toast(
    message,
    isMobilePlatform() ? { style: { fontSize: "14px", height: "20px" } } : {},
  );
};

export const SpeechRecWeb = () => {
  const SpeechRecognition =
    window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SpeechRecognition) {
    alert("Browser tidak mendukung Speech Recognition API");
    return;
  }

  const recognition = new SpeechRecognition();
  recognition.lang = "id-ID";
  recognition.interimResults = false;
  recognition.continuous = false;
  return recognition;
};

export const initSpeechRecMobile = async () => {
  if (!isMobilePlatform()) return;
  try {
    const { available } = await SpeechRecognition.available();
    if (!available) {
      return { enable: false, speech: undefined };
    } else {
      const { speechRecognition } =
        await SpeechRecognition.requestPermissions();
      return {
        enable: speechRecognition == "granted",
        speech: SpeechRecognition,
      };
    }
  } catch (error) {
    console.log(error);
    return { enable: false, speech: undefined };
  }
};
export default useIsMobile;
